/**
 * `fetch` atado a la cuenta cuyos datos sirve la pestaña.
 *
 * Es la última barrera antes de que una petición salga: una petición con el
 * token de una cuenta no sale mientras la pestaña tiene abierta la base local
 * de otra (ni mientras no tiene ninguna). Cubre los casos que la cola no ve:
 * una petición rezagada de la cuenta anterior, un reintento tras un 401 que ha
 * renovado la sesión con la cookie de otra cuenta, u otra pestaña que ha
 * cambiado de cuenta mientras ésta seguía abierta.
 *
 * Además, al cambiar de cuenta se cortan las peticiones con token que seguían
 * en vuelo: cerrar sesión no espera a una red lenta y lo cortado se queda en
 * la cola de su cuenta.
 *
 * Lógica pura, sin `@/` ni DOM más allá de `fetch` y `AbortController`, para
 * poder probarla con `node --test`.
 */
import { bearerBelongsTo } from '@fitness/core';

/** Se ha intentado enviar el token de una cuenta con los datos de otra abiertos. */
export class AccountMismatchError extends Error {
  constructor() {
    super('La petición es de otra cuenta: no se envía.');
    this.name = 'AccountMismatchError';
  }
}

type BaseFetch = (input: Request, init?: RequestInit) => Promise<Response>;

export interface AccountFetch {
  /** `fetch` para `createApiClient`. */
  fetch: (request: Request) => Promise<Response>;
  /** Cambia la cuenta (o `null` al cerrar sesión) y corta lo que seguía en vuelo. */
  bind: (userId: string | null) => void;
  /** Cuenta atada ahora mismo. */
  account: () => string | null;
}

function hasBearer(request: Request): boolean {
  return /^Bearer\s+/i.test(request.headers.get('Authorization') ?? '');
}

/**
 * Crea el `fetch` atado. `onMismatch` se llama cuando llega el token de otra
 * cuenta con una cuenta abierta: la sesión de esta pestaña ya no es la suya.
 */
export function createAccountFetch(
  base: BaseFetch = (input, init) => globalThis.fetch(input, init),
  onMismatch?: () => void,
): AccountFetch {
  let account: string | null = null;
  let controller = new AbortController();

  return {
    account: () => account,

    bind(userId) {
      if (userId === account) return;
      account = userId;
      controller.abort();
      controller = new AbortController();
    },

    fetch(request) {
      if (!bearerBelongsTo(request.headers.get('Authorization'), account)) {
        if (account !== null) onMismatch?.();
        return Promise.reject(new AccountMismatchError());
      }
      if (!hasBearer(request)) return base(request);
      const signal =
        typeof AbortSignal.any === 'function'
          ? AbortSignal.any([request.signal, controller.signal])
          : controller.signal;
      return base(request, { signal });
    },
  };
}
